import { useMemo } from 'react';
import type { IsoDate } from '@/domain/content.types';
import { oddsFor, parseChance } from '@/domain/odds';
import { phaseOf } from '@/domain/schedule';
import { useT } from '@/i18n/useT';
import { useProgress } from '@/state/progressStore';
import { useSettings } from '@/state/settingsStore';
import { useToast } from '@/state/toastStore';
import type { VisibleGroup } from '@/domain/selectors';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { ItemRow } from './ItemRow';
import { phaseLabel, phaseTone } from './phaseLabel';

interface GroupSectionProps {
  setId: string;
  visible: VisibleGroup;
  today: IsoDate;
}

export function GroupSection({ setId, visible, today }: GroupSectionProps) {
  const { t, lang } = useT();
  const { group, items, done, total } = visible;

  const phase = phaseOf(group, today);
  const key = `${setId}/${group.id}`;
  const stored = useSettings((s) => s.collapsed[key]);
  const setCollapsed = useSettings((s) => s.setCollapsed);
  // The user's own choice wins; otherwise a finished week folds itself so the
  // live one is not below a screen of history.
  const collapsed = stored ?? (group.collapsedByDefault === true || phase.kind === 'ended');

  const setMany = useProgress((s) => s.setMany);
  const setProgress = useProgress((s) => s.snapshot.sets[setId]);
  const showToast = useToast((s) => s.show);

  const allIds = group.items.map((i) => i.id);
  const complete = done === total;

  const odds = useMemo(() => {
    const chances: number[] = [];
    for (const item of group.items) {
      if (setProgress?.[item.id]?.done) continue;
      const p = parseChance(item.dropChance);
      if (p !== null) chances.push(p);
    }
    return oddsFor(chances);
  }, [group.items, setProgress]);

  // One decimal while the number is small enough for it to mean something.
  // `round` feeds the plural rule, `fmt` the eye — "3,5" is not a number.
  const round = (n: number): number => (n < 10 ? Math.round(n * 10) / 10 : Math.round(n));
  const fmt = (n: number): string =>
    new Intl.NumberFormat(lang, { maximumFractionDigits: n < 10 ? 1 : 0 }).format(n);

  /**
   * Both bulk buttons are one click away from undoing a week of ticking, so
   * every use offers an undo instead of a confirm: a confirm is a tax on the
   * 99% of clicks that were meant. Undo flips back exactly the ids that
   * changed, so an item the user re-ticked by hand in the meantime is left be.
   */
  const bulk = (value: boolean): void => {
    const changed = setMany(setId, allIds, value);
    if (changed.length === 0) return;
    showToast({
      message: t(value ? 'undo.meowed' : 'undo.unmeowed', { n: changed.length }),
      action: { label: t('undo.action'), run: () => void setMany(setId, changed, !value) },
    });
  };

  return (
    // Deliberately not `overflow-hidden`: the Meow bubble rises above its
    // checkbox, and clipping would decapitate it on the first row of every
    // group. The header carries its own top rounding instead.
    //
    // `scroll-mt` clears the sticky header when the Now card jumps here.
    <section id={`group-${group.id}`} className="mb-3 scroll-mt-36 rounded-sm border border-line bg-surface">
      <header className="flex items-center gap-2 rounded-t-sm border-b border-line bg-surface-2/60 px-2 py-1.5">
        <button
          type="button"
          onClick={() => setCollapsed(setId, group.id, !collapsed)}
          aria-expanded={!collapsed}
          aria-label={collapsed ? t('group.expand') : t('group.collapse')}
          className="shrink-0 rounded-xs px-1 text-text-faint transition-transform hover:text-pip"
        >
          <span className={`inline-block transition-transform ${collapsed ? '' : 'rotate-90'}`}>
            ▶
          </span>
        </button>

        <div className="min-w-0 flex-1">
          <h2 className="truncate text-[13px] leading-5 font-semibold text-text">
            {group.title[lang]}
          </h2>
          {group.subtitle || phase.kind !== 'always' ? (
            <div className="flex min-w-0 items-center gap-1.5">
              {group.subtitle ? (
                <p className="truncate text-[11px] leading-4 text-text-faint">
                  {group.subtitle[lang]}
                </p>
              ) : null}
              {phase.kind !== 'always' ? (
                <span
                  className={`shrink-0 rounded-xs border px-1 text-[10px] leading-4 ${phaseTone(phase)}`}
                >
                  {phaseLabel(t, phase)}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="flex w-24 shrink-0 flex-col items-end gap-1">
          <span className={`tnum text-[11px] leading-4 ${complete ? 'text-pip' : 'text-text-dim'}`}>
            {t('stats.collected', { done, total })}
          </span>
          <ProgressBar done={done} total={total} size="sm" />
        </div>

        <div className="flex shrink-0 gap-1">
          <button
            type="button"
            onClick={() => bulk(true)}
            title={t('meow.all')}
            aria-label={t('meow.all')}
            className="rounded-xs border border-line px-1.5 py-0.5 text-[11px] leading-4 text-text-faint hover:border-pip-dim hover:text-pip"
          >
            ✓
          </button>
          <button
            type="button"
            onClick={() => bulk(false)}
            title={t('meow.none')}
            aria-label={t('meow.none')}
            className="rounded-xs border border-line px-1.5 py-0.5 text-[11px] leading-4 text-text-faint hover:border-danger/50 hover:text-danger"
          >
            ✕
          </button>
        </div>
      </header>

      {collapsed ? null : (
        <>
          {group.note ? (
            <p className="border-b border-line-soft bg-surface-2/30 px-3 py-1.5 text-[11px] leading-4 text-text-faint">
              {group.note[lang]}
            </p>
          ) : null}
          {/*
            Only while there is something left to roll for. Disappears with
            the last tick, which is its own small reward.
          */}
          {odds ? (
            <p
              title={t('odds.explain')}
              className="tnum border-b border-line-soft px-3 py-1 text-[11px] leading-4 text-text-dim"
            >
              <span className="font-display text-[10px] tracking-wide text-pip-dim uppercase">
                {t('odds.label')}
              </span>
              {' · '}
              {t('stats.remaining', { n: odds.remaining })}
              {' · '}
              {t('odds.expected', { n: round(odds.expected), value: fmt(odds.expected) })}
              {' · '}
              {t('odds.ninety', { value: fmt(odds.ninety) })}
            </p>
          ) : null}
          <ul>
            {items.map((item) => (
              <ItemRow key={item.id} setId={setId} item={item} />
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
